<template>
  <div>
    <Head :title="`Compra #${shopping.id} ${shopping.status === 'cancelled' ? '| COMPRA ANULADA' : ''}`" />

    <!-- Header & Actions -->
    <div class="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <div>
        <h1 class="text-3xl font-bold text-gray-800 flex items-center gap-2">
          <Link href="/shoppings" class="text-gray-400 hover:text-orange-500 transition-colors font-bold">
            Compras
          </Link>
          <span class="text-gray-300 font-light">/</span>
          <span>
            Compra #{{ shopping.id }}
            <span v-if="shopping.status === 'cancelled'" class="text-red-500 text-lg font-medium ml-1">(ANULADA)</span>
            <span v-else-if="shopping.status === 'paid'" class="text-green-600 text-lg font-medium ml-1">(PAGADA)</span>
          </span>
        </h1>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <!-- Nueva Compra -->
        <Link
          href="/shoppings/create"
          class="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm"
        >
          <i class="fa fa-plus" />
          <span class="hidden sm:inline">Nueva compra</span>
        </Link>

        <!-- Imprimir -->
        <a
          target="_blank" :href="`/shoppings/${shopping.id}/report`"
          class="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm"
        >
          <i class="fa fa-print text-gray-500" />
          <span class="hidden sm:inline">Imprimir</span>
        </a>
      </div>
    </div>

    <!-- Provider Info & Details -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-6 p-4 sm:p-6 bg-gradient-to-br from-teal-50 to-white rounded-2xl shadow-lg border border-teal-100">
      <!-- Proveedor -->
      <div class="flex flex-col gap-4 bg-white rounded-xl shadow p-4 border border-teal-100">
        <div class="flex items-center gap-2 mb-2">
          <i class="fa fa-truck text-2xl text-teal-600" />
          <span class="font-semibold text-teal-800 text-lg">Proveedor</span>
        </div>
        <div class="flex items-center gap-2">
          <i class="fa fa-building text-gray-400" />
          <span class="font-semibold">{{ shopping.provider?.company_name ?? shopping.provider?.name }}</span>
        </div>
        <div class="flex items-center gap-2">
          <i class="fa fa-id-card text-gray-400" />
          <span class="text-gray-800 text-sm">{{ shopping.provider?.tax_id || '-' }}</span>
        </div>
        <div class="flex items-center gap-2">
          <i class="fa fa-envelope text-gray-400" />
          <span class="text-gray-800 text-sm">{{ shopping.provider?.email || '-' }}</span>
        </div>
        <div class="flex items-center gap-2">
          <i class="fa fa-phone text-gray-400" />
          <span class="text-gray-800 text-sm">{{ shopping.provider?.phone || '-' }}</span>
        </div>
      </div>

      <!-- Totales -->
      <div class="flex flex-col gap-3 bg-white rounded-xl shadow p-4 border border-teal-100">
        <div class="flex items-center gap-2 mb-2">
          <i class="fa fa-dollar-sign text-2xl text-teal-600" />
          <span class="font-semibold text-teal-800 text-lg">Totales</span>
        </div>
        <div class="flex items-center justify-between">
          <span class="font-semibold text-gray-600">Total:</span>
          <span class="font-bold text-gray-900">{{ formatCurrency(shopping.total) }}</span>
        </div>
        <div class="flex items-center justify-between">
          <span class="font-semibold text-gray-600">Pagado:</span>
          <span class="font-bold text-teal-600">{{ formatCurrency(total_paid) }}</span>
        </div>
        <div class="flex items-center justify-between border-t pt-2 mt-1">
          <span class="font-semibold text-gray-800">Saldo Pendiente:</span>
          <span class="font-bold text-red-600">{{ formatCurrency(remainingBalance) }}</span>
        </div>
      </div>

      <!-- Detalles -->
      <div class="flex flex-col gap-3 bg-white rounded-xl shadow p-4 border border-teal-100">
        <div class="flex items-center gap-2 mb-2">
          <i class="fa fa-info-circle text-2xl text-teal-600" />
          <span class="font-semibold text-teal-800 text-lg">Detalles</span>
        </div>
        <div class="flex items-center gap-2">
          <i class="fa fa-file-invoice text-gray-400" />
          <span class="font-semibold">Factura:</span>
          <span>#{{ shopping.invoice_number }}</span>
        </div>
        <div class="flex items-center gap-2">
          <i class="fa fa-calendar-alt text-gray-400" />
          <span class="font-semibold">Fecha:</span>
          <span>{{ formatDate(shopping.date) }}</span>
        </div>
        <div class="flex items-center gap-2">
          <i class="fa fa-calendar-check text-gray-400" />
          <span class="font-semibold">Vencimiento:</span>
          <span>{{ formatDate(shopping.due_date) }}</span>
        </div>
        <div class="flex items-center gap-2">
          <i class="fa fa-flag text-gray-400" />
          <span class="font-semibold">Estado:</span>
          <span
            :class="[
              'px-2 py-0.5 rounded text-xs font-bold uppercase',
              getStatusBadgeClass(shopping.status)
            ]"
          >
            {{ getStatusLabel(shopping.status) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Products Section -->
    <div class="p-4 sm:p-6 bg-white rounded-2xl shadow-lg border border-teal-100 mb-6">
      <h2 class="text-xl font-bold mb-6 flex items-center gap-2">
        <i class="fa fa-box text-teal-600" /> Productos
      </h2>
      <div class="overflow-x-auto rounded-lg border border-teal-100">
        <table class="w-full min-w-max">
          <thead class="bg-teal-50">
            <tr>
              <th class="py-4 px-6 text-left text-teal-900 font-bold uppercase text-xs tracking-wider">Código</th>
              <th class="py-4 px-6 text-left text-teal-900 font-bold uppercase text-xs tracking-wider">Producto</th>
              <th class="py-4 px-6 text-center text-teal-900 font-bold uppercase text-xs tracking-wider">Cantidad</th>
              <th class="py-4 px-6 text-right text-teal-900 font-bold uppercase text-xs tracking-wider">Precio Unitario</th>
              <th class="py-4 px-6 text-right text-teal-900 font-bold uppercase text-xs tracking-wider">Subtotal</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr
              v-for="product in shopping.products"
              :key="product.id"
              class="hover:bg-gray-50 transition-colors"
            >
              <td class="py-4 px-6 font-mono text-sm text-gray-600">{{ product.product.code }}</td>
              <td class="py-4 px-6 font-medium text-gray-900">{{ product.product.name }}</td>
              <td class="py-4 px-6 text-center">{{ product.quantity }}</td>
              <td class="py-4 px-6 text-right">{{ formatCurrency(product.price) }}</td>
              <td class="py-4 px-6 text-right font-bold text-gray-900">{{ formatCurrency(product.price * product.quantity) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="bg-teal-50/50 font-bold">
              <td colspan="4" class="py-4 px-6 text-right text-teal-900">TOTAL COMPRA:</td>
              <td class="py-4 px-6 text-right text-teal-900 text-lg">{{ formatCurrency(shopping.total) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Payments Section -->
    <div class="mb-6 p-4 sm:p-6 bg-white rounded-2xl shadow-lg border border-teal-100">
      <h2 class="text-xl font-bold mb-6 flex items-center gap-2">
        <i class="fa fa-receipt text-teal-600" /> Pagos Realizados
      </h2>
      <div class="overflow-x-auto rounded-lg border border-teal-100">
        <table class="w-full min-w-max">
          <thead class="bg-teal-50">
            <tr>
              <th class="py-4 px-6 text-left text-teal-900 font-bold uppercase text-xs tracking-wider">Fecha</th>
              <th class="py-4 px-6 text-left text-teal-900 font-bold uppercase text-xs tracking-wider">Método</th>
              <th class="py-4 px-6 text-right text-teal-900 font-bold uppercase text-xs tracking-wider">Monto USD</th>
              <th class="py-4 px-6 text-right text-teal-900 font-bold uppercase text-xs tracking-wider">Monto Bs.</th>
              <th class="py-4 px-6 text-center text-teal-900 font-bold uppercase text-xs tracking-wider">Tasa</th>
              <th class="py-4 px-6 text-left text-teal-900 font-bold uppercase text-xs tracking-wider">Referencia</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="payment in shopping.payments" :key="payment.id" class="hover:bg-gray-50 transition-colors">
              <td class="py-4 px-6 text-sm text-gray-600">{{ formatDate(payment.created_at) }}</td>
              <td class="py-4 px-6 font-medium text-gray-900">{{ getPaymentMethodName(payment.payment_method_id) }}</td>
              <td class="py-4 px-6 text-right font-bold text-gray-900">{{ formatCurrency(payment.amount_usd) }}</td>
              <td class="py-4 px-6 text-right text-gray-600">{{ payment.amount_ves ? formatCurrency(payment.amount_ves, 'VES') : '-' }}</td>
              <td class="py-4 px-6 text-center text-gray-500">{{ payment.exchange_rate ? payment.exchange_rate : '-' }}</td>
              <td class="py-4 px-6 text-gray-600">{{ payment.reference || '-' }}</td>
            </tr>
            <tr v-if="!shopping.payments.length">
              <td colspan="6" class="py-12 text-center text-gray-400 italic">
                No hay pagos registrados para esta compra.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Register New Payment -->
    <div v-if="canManagePayments" class="mt-6 p-4 sm:p-6 bg-white rounded-2xl shadow-lg border border-teal-100">
      <h2 class="text-xl font-bold mb-6 flex items-center gap-2 text-gray-800">
        <i class="fa fa-plus-circle text-teal-600" /> Registrar Nuevo Pago
      </h2>

      <div class="bg-teal-50 border border-teal-100 p-4 rounded-xl mb-6 flex items-center gap-3">
        <i class="fa fa-info-circle text-teal-600 text-lg" />
        <p class="text-teal-800 font-medium">
          Saldo pendiente por pagar: <span class="text-xl font-bold ml-1 text-teal-900">{{ formatCurrency(remainingBalance) }}</span>
        </p>
      </div>

      <shopping-payment-box 
        ref="paymentBox"
        :payment_methods="payment_methods"
        :balance="remainingBalance"
        :change="current_exchange_rate"
        @add-payment="addPayment"
      />
    </div>

    <!-- Actions Footer -->
    <div v-if="shopping.status !== 'cancelled' && shopping.status !== 'paid'" class="mt-6 p-6 bg-white rounded-2xl shadow-lg border border-red-100 flex justify-end">
      <button
        class="px-6 py-2 bg-white border border-red-200 text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-2 transition-colors font-bold shadow-sm"
        @click="cancelShopping"
      >
        <i class="fa fa-ban" />
        Anular Compra
      </button>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import ShoppingPaymentBox from '@/Shared/ShoppingPaymentBox'
import { Inertia } from '@inertiajs/inertia'
import Swal from 'sweetalert2'

export default {
  name: 'ShoppingShow',

  components: {
    Head,
    Link,
    ShoppingPaymentBox,
  },

  layout: Layout,

  props: {
    shopping: {
      type: Object,
      required: true,
    },
    total_paid: {
      type: Number,
      required: true,
    },
    payment_methods: {
      type: Array,
      required: true,
    },
    current_exchange_rate: {
      type: Number,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
  },

  computed: {
    canManagePayments() {
      return ['financial', 'admin', 'sales'].includes(this.user.role) && 
             this.shopping.status !== 'paid' && 
             this.shopping.status !== 'cancelled'
    },

    remainingBalance() {
      return Math.max(0, this.shopping.total - this.total_paid)
    },
  },

  methods: {
    formatCurrency(value, currency = 'USD') {
      const config = currency === 'USD' 
        ? { style: 'currency', currency: 'USD', locales: 'es-US' }
        : { style: 'currency', currency: 'VES', locales: 'es-VE' }
      
      return new Intl.NumberFormat(config.locales, {
        style: config.style,
        currency: config.currency,
      }).format(value || 0)
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },

    getPaymentMethodName(methodId) {
      const method = this.payment_methods.find(m => m.id === methodId)
      return method ? method.name : 'Desconocido'
    },

    getStatusLabel(status) {
      const labels = {
        pending: 'Pendiente',
        cancelled: 'Anulada',
        paid: 'Pagada',
        completed: 'Completada',
      }
      return labels[status] || status
    },

    getStatusBadgeClass(status) {
      switch(status) {
      case 'completed': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      case 'paid': return 'bg-teal-100 text-teal-800'
      default: return 'bg-gray-100 text-gray-800'
      }
    },

    addPayment(payment) {
      const payload = {
        shopping_id: this.shopping.id,
        method: payment.method,
        amount_usd: parseFloat(payment.amount) || 0,
        amount_ves: payment.amount_bs || 0,
        exchange_rate: this.current_exchange_rate,
        reference: payment.reference || '',
      }

      if (payload.amount_usd > this.remainingBalance + 0.01) { // Pequeño margen para errores de redondeo
        Swal.fire({
          icon: 'warning',
          title: 'Monto excedido',
          text: 'El monto del pago excede el saldo adeudado',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#0d9488', // teal-600
        })
        return
      }

      Inertia.post('/shoppings/pay', payload, {
        onSuccess: () => {
          if (this.$refs.paymentBox) {
            this.$refs.paymentBox.resetForm()
          }
          Swal.fire({
            icon: 'success',
            title: 'Pago registrado',
            text: 'El pago se ha procesado correctamente',
            timer: 2000,
            showConfirmButton: false,
          })
        },
        preserveScroll: true,
        preserveState: false,
      })
    },

    back() {
      this.$inertia.visit('/shoppings')
    },

    cancelShopping() {
      Swal.fire({
        title: '¿Está seguro?',
        text: '¿Desea anular esta compra? Esta acción no se puede deshacer.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, anular',
        cancelButtonText: 'Cancelar',
      }).then((result) => {
        if (result.isConfirmed) {
          this.$inertia.get(`/shoppings/${this.shopping.id}/cancel`)
        }
      })
    },
  },
}
</script>
